import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  X,
  ArrowRight,
  Zap,
  Code,
  Brain,
  Wifi,
  TrendingUp,
  Mail,
  Phone,
  MapPin,
  Check,
} from "lucide-react";

// 3D Canvas Component for geometric background
function GeometricCanvas() {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Fallback to CSS-based geometric animation
    const canvas = containerRef.current;
    canvas.innerHTML = `
      <div style="position: absolute; inset: 0; overflow: hidden;">
        <style>
          @keyframes float1 { 0%, 100% { transform: translateY(0px) rotateZ(0deg); } 50% { transform: translateY(-40px) rotateZ(5deg); } }
          @keyframes float2 { 0%, 100% { transform: translateY(0px) rotateZ(0deg); } 50% { transform: translateY(40px) rotateZ(-5deg); } }
          @keyframes float3 { 0%, 100% { transform: translateY(0px) rotateZ(0deg); } 50% { transform: translateY(-25px) rotateZ(10deg); } }
          @keyframes pulse-glow { 0%, 100% { opacity: 0.08; } 50% { opacity: 0.15; } }

          .geo-shape { position: absolute; opacity: 0.08; mix-blend-mode: screen; }
          .shape-1 { animation: float1 8s ease-in-out infinite; }
          .shape-2 { animation: float2 10s ease-in-out infinite; }
          .shape-3 { animation: float3 12s ease-in-out infinite; }
          .shape-pulse { animation: pulse-glow 4s ease-in-out infinite; }
        </style>

        <svg class="geo-shape shape-1 shape-pulse" style="top: 5%; left: 5%; width: 350px; height: 350px;" viewBox="0 0 300 300">
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#ff6b35;stop-opacity:0.6" />
              <stop offset="100%" style="stop-color:#f97316;stop-opacity:0.2" />
            </linearGradient>
          </defs>
          <polygon points="150,20 280,150 150,280 20,150" fill="none" stroke="url(#grad1)" stroke-width="2"/>
          <circle cx="150" cy="150" r="80" fill="none" stroke="#ff6b35" stroke-width="1.5" opacity="0.4"/>
          <polygon points="150,80 210,140 150,200 90,140" fill="none" stroke="#ff6b35" stroke-width="1" opacity="0.3"/>
        </svg>

        <svg class="geo-shape shape-2 shape-pulse" style="top: 55%; right: 8%; width: 320px; height: 320px;" viewBox="0 0 300 300">
          <defs>
            <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#0ea5e9;stop-opacity:0.5" />
              <stop offset="100%" style="stop-color:#06b6d4;stop-opacity:0.1" />
            </linearGradient>
          </defs>
          <circle cx="150" cy="150" r="120" fill="none" stroke="url(#grad2)" stroke-width="2"/>
          <circle cx="150" cy="150" r="80" fill="none" stroke="#0ea5e9" stroke-width="1.5" opacity="0.3"/>
          <circle cx="150" cy="150" r="40" fill="none" stroke="#0ea5e9" stroke-width="1" opacity="0.2"/>
          <line x1="30" y1="150" x2="270" y2="150" stroke="#0ea5e9" stroke-width="0.8" opacity="0.2"/>
          <line x1="150" y1="30" x2="150" y2="270" stroke="#0ea5e9" stroke-width="0.8" opacity="0.2"/>
        </svg>

        <svg class="geo-shape shape-3 shape-pulse" style="top: 15%; right: 12%; width: 280px; height: 280px;" viewBox="0 0 300 300">
          <defs>
            <linearGradient id="grad3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#10b981;stop-opacity:0.4" />
              <stop offset="100%" style="stop-color:#059669;stop-opacity:0.1" />
            </linearGradient>
          </defs>
          <polygon points="150,30 270,90 240,210 60,210 30,90" fill="none" stroke="url(#grad3)" stroke-width="2"/>
          <line x1="150" y1="30" x2="60" y2="210" stroke="#10b981" stroke-width="0.8" opacity="0.15"/>
          <line x1="150" y1="30" x2="240" y2="210" stroke="#10b981" stroke-width="0.8" opacity="0.15"/>
        </svg>

        <svg class="geo-shape shape-1 shape-pulse" style="bottom: 5%; left: 15%; width: 300px; height: 300px;" viewBox="0 0 400 400">
          <defs>
            <radialGradient id="rad1">
              <stop offset="0%" style="stop-color:#ff6b35;stop-opacity:0.3" />
              <stop offset="100%" style="stop-color:#0ea5e9;stop-opacity:0.05" />
            </radialGradient>
          </defs>
          <rect x="50" y="50" width="300" height="300" fill="none" stroke="#ff6b35" stroke-width="1.5" opacity="0.3" transform="rotate(45 200 200)"/>
          <circle cx="200" cy="200" r="150" fill="url(#rad1)" stroke="#0ea5e9" stroke-width="1" opacity="0.3"/>
          <circle cx="200" cy="200" r="100" fill="none" stroke="#ff6b35" stroke-width="0.8" opacity="0.2" stroke-dasharray="5,5"/>
        </svg>
      </div>
    `;
  }, []);

  return <div ref={containerRef} className="absolute inset-0" />;
}

export default function App() {
  const [currentPage, setCurrentPage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: "Home", id: "home" },
    { name: "About", id: "about" },
    { name: "Services", id: "services" },
    { name: "Contact", id: "contact" },
  ];

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return <HomePage onNavigate={setCurrentPage} />;
      case "about":
        return <AboutPage />;
      case "services":
        return <ServicesPage />;
      case "contact":
        return <ContactPage />;
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Space+Mono:wght@400;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html, body {
          scroll-behavior: smooth;
        }

        .font-display {
          font-family: 'Inter', sans-serif;
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .font-mono {
          font-family: 'Space Mono', monospace;
        }

        .gradient-text {
          background: linear-gradient(135deg, #fff 0%, #cbd5e1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .accent-gradient {
          background: linear-gradient(135deg, #ff6b35 0%, #f97316 100%);
        }

        .card-glass-hover {
          background: rgba(30, 41, 59, 0.6);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(148, 163, 184, 0.12);
        }

        .hover-lift {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .hover-lift:hover {
          transform: translateY(-12px);
          background: rgba(30, 41, 59, 0.8);
          border-color: rgba(255, 107, 53, 0.4);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.7s ease-out;
        }

        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.3s; }
      `}</style>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-gradient-to-b from-slate-950/95 to-slate-950/50 backdrop-blur border-b border-slate-800/20">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <button
            onClick={() => setCurrentPage("home")}
            className="flex items-center gap-2.5 group"
          >
            <div className="w-9 h-9 rounded-lg accent-gradient group-hover:shadow-lg group-hover:shadow-orange-500/30 transition-all"></div>
            <span className="font-display text-2xl font-black">Surrvik</span>
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-10">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`text-sm font-bold transition-all duration-300 relative group ${
                  currentPage === item.id
                    ? "text-orange-400"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {item.name}
                {currentPage === item.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 to-orange-400"></div>
                )}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage("contact")}
              className="px-6 py-2.5 rounded-lg accent-gradient text-white font-bold text-sm hover:shadow-lg hover:shadow-orange-500/40 transition-all"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 hover:bg-slate-800/30 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-800/30 bg-slate-900/80 backdrop-blur">
            <div className="px-6 py-4 flex flex-col gap-3">
              {navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentPage(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`text-left py-2 font-bold ${
                    currentPage === item.id
                      ? "text-orange-400"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Page Content */}
      {renderPage()}

      {/* Footer */}
      <footer className="border-t border-slate-800/30 bg-gradient-to-t from-slate-950 via-slate-900/50 to-transparent relative z-20 mt-20">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
            <div>
              <h3 className="font-display text-lg mb-4">Surrvik</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Intelligent mobility solutions and secure software systems for
                tomorrow's challenges.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-4 text-slate-200">
                Connect With Us
              </h4>
              <div className="flex gap-3"></div>
            </div>
          </div>
          <div className="border-t border-slate-800/30 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500">
            <p>&copy; 2026 Surrvik e-Mobility. All rights reserved.</p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <button
                onClick={() => setCurrentPage("contact")}
                className="hover:text-slate-300 transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function HomePage({ onNavigate }) {
  return (
    <main>
      <section className="min-h-[calc(100vh-80px)] flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <GeometricCanvas />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/20 via-transparent to-slate-950/60"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center py-20">
          <h1 className="font-display text-6xl md:text-8xl mb-8 leading-tight animate-fade-in">
            <span className="gradient-text">
              The Future of Intelligent Mobility
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 mb-12 max-w-3xl mx-auto animate-fade-in delay-1 leading-relaxed">
            Next-generation software systems, AI-powered platforms, and secure
            SaaS solutions that power the future of connected ecosystems.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in delay-2 flex-wrap">
            <button
              onClick={() => onNavigate("services")}
              className="px-8 py-4 accent-gradient text-white font-bold rounded-lg hover:shadow-2xl hover:shadow-orange-500/40 transition-all flex items-center justify-center gap-3 group text-lg"
            >
              Explore Solutions
              <ArrowRight
                size={22}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <button
              onClick={() => onNavigate("about")}
              className="px-8 py-4 border-2 border-slate-600 hover:border-orange-400 text-white font-bold rounded-lg transition-all text-lg hover:bg-orange-500/5"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 relative z-20">
        <div className="text-center mb-20">
          <h2 className="font-display text-5xl md:text-6xl mb-6 animate-fade-in">
            Core Capabilities
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto animate-fade-in delay-1">
            Comprehensive solutions designed for innovation and scalability
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: Zap,
              title: "E-Mobility Solutions",
              desc: "Next-gen electric mobility platforms and infrastructure",
            },
            {
              icon: Code,
              title: "SaaS Development",
              desc: "Secure, scalable cloud applications built for performance",
            },
            {
              icon: Brain,
              title: "AI & Intelligence",
              desc: "Machine learning and predictive analytics integration",
            },
            {
              icon: Wifi,
              title: "IoT Systems",
              desc: "Connected device ecosystems and smart networks",
            },
            {
              icon: TrendingUp,
              title: "Performance",
              desc: "Optimization and intelligent scaling solutions",
            },
            {
              icon: Code,
              title: "Digital Services",
              desc: "SEO strategy and digital presence optimization",
            },
          ].map((service, i) => {
            const Icon = service.icon;
            return (
              <div
                key={i}
                className="card-glass-hover p-8 rounded-2xl hover-lift border border-slate-700/50"
              >
                <Icon className="w-12 h-12 text-orange-500 mb-5" />
                <h3 className="font-bold text-xl mb-3 text-white">
                  {service.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">{service.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 relative z-20">
        <div className="text-center mb-12">
          <p className="text-sm font-bold uppercase tracking-widest text-orange-400 mb-3">
            Trusted By
          </p>
          <h2 className="font-display text-4xl md:text-5xl">
            Our Trusted Clients
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["Excellenza", "TTL Engineering", "Pragya Yoga", "Maanjoo Farms"].map(
            (client, i) => (
              <div
                key={i}
                className="px-6 py-6 border-2 border-slate-700/50 hover:border-orange-500/50 rounded-xl text-center font-bold text-slate-400 hover:text-white transition-all cursor-default"
              >
                {client}
              </div>
            ),
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-20 relative z-20">
        <div className="card-glass-hover border-2 border-orange-500/20 rounded-3xl p-16 text-center">
          <h2 className="font-display text-5xl md:text-6xl mb-8">Our Vision</h2>
          <p className="text-xl text-slate-300 leading-relaxed">
            To build a future where mobility, intelligence, and technology
            converge seamlessly to create scalable and sustainable ecosystems
            that drive progress for tomorrow.
          </p>
        </div>
      </section>
    </main>
  );
}

function AboutPage() {
  return (
    <main>
      <section className="min-h-[50vh] flex items-center justify-center relative overflow-hidden pt-20">
        <div className="absolute inset-0 z-0 opacity-30">
          <GeometricCanvas />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 to-slate-950/90"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-20">
          <h1 className="font-display text-6xl md:text-7xl mb-6 gradient-text">
            About Surrvik
          </h1>
          <p className="text-xl text-slate-300">
            Building intelligent systems that power tomorrow's innovations
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24 relative z-20">
        <div className="mb-20">
          <p className="text-xl text-slate-300 leading-relaxed mb-8 animate-fade-in">
            Surrvik e-Mobility is a forward-thinking technology company
            dedicated to building intelligent, secure, and scalable systems that
            power the future of connected mobility and intelligent ecosystems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-24">
          <div className="card-glass-hover p-10 rounded-2xl border border-slate-700/50 animate-fade-in">
            <div className="w-14 h-14 rounded-xl accent-gradient flex items-center justify-center mb-6">
              <Zap className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-display text-3xl mb-4">Our Mission</h3>
            <p className="text-slate-400 leading-relaxed text-lg">
              To design and deploy innovative solutions that integrate
              next-generation software, AI-powered platforms, and secure
              infrastructure for smart mobility ecosystems.
            </p>
          </div>
          <div className="card-glass-hover p-10 rounded-2xl border border-slate-700/50 animate-fade-in delay-1">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-6">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-display text-3xl mb-4">Our Approach</h3>
            <p className="text-slate-400 leading-relaxed text-lg">
              We combine cutting-edge technology with deep domain expertise to
              create solutions that are not just innovative, but transformative
              for our clients and partners.
            </p>
          </div>
        </div>

        <div className="mb-24">
          <h2 className="font-display text-5xl mb-12">Why Choose Surrvik</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Expertise in e-mobility and IoT ecosystems",
              "Secure-by-design SaaS platforms",
              "AI and machine learning integration",
              "Proven track record with leading companies",
              "24/7 technical support and guidance",
              "Scalable solutions for growing businesses",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-4 card-glass-hover p-6 rounded-xl border border-slate-700/50"
              >
                <div className="w-8 h-8 rounded-full accent-gradient flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg text-slate-300 font-medium">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Happy Clients", value: "50+" },
            { label: "Projects Delivered", value: "200+" },
            { label: "Team Members", value: "45+" },
            { label: "Years of Experience", value: "5+" },
          ].map((stat, i) => (
            <div
              key={i}
              className="card-glass-hover p-8 rounded-2xl text-center border border-slate-700/50 hover-lift"
            >
              <p className="text-4xl font-display text-orange-400 mb-3">
                {stat.value}
              </p>
              <p className="text-slate-400 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function ServicesPage() {
  return (
    <main>
      <section className="min-h-[40vh] flex items-center justify-center relative overflow-hidden pt-20">
        <div className="absolute inset-0 z-0 opacity-20">
          <GeometricCanvas />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 to-slate-950/90"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-16">
          <h1 className="font-display text-6xl md:text-7xl mb-6 gradient-text">
            Our Services
          </h1>
          <p className="text-xl text-slate-300">
            Comprehensive solutions tailored to your unique challenges
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24 relative z-20">
        <div className="space-y-12">
          {[
            {
              title: "E-Mobility & Infrastructure",
              desc: "End-to-end electric mobility solutions including platform development, fleet management, and charging infrastructure optimization.",
              features: [
                "Fleet Management",
                "Smart Charging",
                "Vehicle Analytics",
              ],
              icon: Zap,
            },
            {
              title: "SaaS & Software Development",
              desc: "Custom SaaS platforms built with security, scalability, and performance at the core. From concept to production.",
              features: [
                "Custom Development",
                "Cloud Architecture",
                "API Integration",
              ],
              icon: Code,
            },
            {
              title: "AI-Based Innovations",
              desc: "Machine learning and AI solutions that enhance decision-making, predict outcomes, and automate complex processes.",
              features: [
                "ML Models",
                "Predictive Analytics",
                "Computer Vision",
              ],
              icon: Brain,
            },
            {
              title: "Robotics & IoT",
              desc: "Connected device ecosystems and robotics solutions for automation and intelligent data collection.",
              features: [
                "IoT Platforms",
                "Edge Computing",
                "Device Management",
              ],
              icon: Wifi,
            },
            {
              title: "Digital Services",
              desc: "SEO, digital marketing, and online presence optimization to reach your target audience effectively.",
              features: ["SEO Strategy", "Content Marketing", "Analytics"],
              icon: TrendingUp,
            },
          ].map((service, i) => {
            const Icon = service.icon;
            return (
              <div
                key={i}
                className="card-glass-hover p-12 rounded-3xl border border-slate-700/50 hover-lift animate-fade-in"
              >
                <div className="flex items-start gap-8 flex-col md:flex-row">
                  <div className="w-16 h-16 rounded-2xl accent-gradient flex items-center justify-center flex-shrink-0">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-3xl mb-4">
                      {service.title}
                    </h3>
                    <p className="text-slate-400 text-lg leading-relaxed mb-6">
                      {service.desc}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {service.features.map((feature, j) => (
                        <span
                          key={j}
                          className="px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full text-sm text-orange-300 font-medium"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function ContactPage() {
  const handleSubmit = (e) => {
    e.preventDefault();
    const el = e.target.elements;
    const name = el.name.value.trim();
    const email = el.email.value.trim();
    const subject = el.subject.value.trim();
    const message = el.message.value.trim();
    const body = `Name: ${name}\nEmail: ${email}\n\n${message}`;
    window.location.href = `mailto:hello@surrvik.com?subject=${encodeURIComponent(
      subject || "Website enquiry",
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <main>
      <section className="min-h-[40vh] flex items-center justify-center relative overflow-hidden pt-20">
        <div className="absolute inset-0 z-0 opacity-20">
          <GeometricCanvas />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 to-slate-950/90"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-16">
          <h1 className="font-display text-6xl md:text-7xl mb-6 gradient-text">
            Get In Touch
          </h1>
          <p className="text-xl text-slate-300">
            Let's discuss how we can help you build the future
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-20">
          <div className="animate-fade-in">
            <h3 className="font-display text-4xl mb-12">Contact Information</h3>
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-xl accent-gradient flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-400 font-bold uppercase tracking-wider mb-2">
                    Email
                  </p>
                  <p className="text-xl font-bold text-white">
                    hello@surrvik.com
                  </p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-400 font-bold uppercase tracking-wider mb-2">
                    Phone
                  </p>
                  <p className="text-xl font-bold text-white">+91 7995581566</p>
                </div>
              </div>
            </div>
          </div>

          <div className="animate-fade-in delay-1">
            <div className="card-glass-hover p-10 rounded-3xl border border-slate-700/50">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-3">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="w-full px-5 py-3 bg-slate-800/50 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500 transition-colors text-white placeholder-slate-500 font-medium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-3">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full px-5 py-3 bg-slate-800/50 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500 transition-colors text-white placeholder-slate-500 font-medium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-3">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    placeholder="How can we help?"
                    className="w-full px-5 py-3 bg-slate-800/50 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500 transition-colors text-white placeholder-slate-500 font-medium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-3">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    placeholder="Tell us about your project..."
                    rows="5"
                    className="w-full px-5 py-3 bg-slate-800/50 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500 transition-colors text-white placeholder-slate-500 font-medium resize-none"
                  ></textarea>
                </div>
                <button className="w-full px-6 py-4 accent-gradient text-white font-bold rounded-xl hover:shadow-2xl hover:shadow-orange-500/40 transition-all text-lg">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
